import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Copy, Loader2, Link2, CheckCircle2, FileText, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { generatedFilePath } from '@/components/workflows/docProvenance';
import { useAuth } from '@/contexts/AuthContext';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { downloadDocumentById } from '@/lib/downloadDocumentById';
import { SealApplyCheckbox } from '@/components/workflows/SealApplyCheckbox';
import { useAssociationSeal } from '@/components/workflows/useAssociationSeal';
import { MEMBER_APPLICATIONS_ENABLED, APPLICATION_FORMATS_APPROVED } from '@/features/memberApplications/flags';
import { DocumentSigningControl } from '@/components/workflows/DocumentSigningControl';
import {
  INVITE_TEACHING,
  DECLINE_NOTE,
  MINOR_NOTE,
  AFFIRMATIONS,
  ageFromDateOfBirth,
  isMinor,
  certificateNumber,
} from '@/features/memberApplications/applicationContent';
import {
  buildApprovalOutputs,
  type ApplicationOutput,
} from '@/features/memberApplications/applicationDocumentHtml';

function makeToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function MemberApplications() {
  const { user } = useAuth();
  const [unaId, setUnaId] = useState<string | null>(null);
  const seal = useAssociationSeal(unaId);
  const [una, setUna] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invites, setInvites] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [openApplication, setOpenApplication] = useState<any | null>(null);
  const [approving, setApproving] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [includeCertificate, setIncludeCertificate] = useState(true);

  const [form, setForm] = useState({ prospect_name: '', prospect_email: '', personal_note: '' });

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!MEMBER_APPLICATIONS_ENABLED) {
    return <Navigate to="/una-members" replace />;
  }

  async function load() {
    setLoading(true);
    try {
      const { data: unaRow } = await supabase
        .from('unas')
        .select('id, name, primary_officer_role')
        .eq('treasury_secretary_id', user?.id)
        .maybeSingle();
      if (!unaRow) {
        setLoading(false);
        return;
      }
      setUna(unaRow);
      setUnaId(unaRow.id);

      const [{ data: inviteRows }, { data: appRows }] = await Promise.all([
        supabase
          .from('prospect_invites')
          .select('*')
          .eq('una_id', unaRow.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('prospect_applications')
          .select('*')
          .eq('una_id', unaRow.id)
          .order('submitted_at', { ascending: false }),
      ]);
      setInvites(inviteRows || []);
      setApplications(appRows || []);
    } finally {
      setLoading(false);
    }
  }

  function linkFor(token: string) {
    return `${window.location.origin}/apply/${token}`;
  }

  async function createInvite() {
    if (!unaId) return;
    if (!form.prospect_name.trim() || !form.prospect_email.trim()) {
      toast.error('Please add a name and an email.');
      return;
    }
    setSaving(true);
    try {
      const token = makeToken();
      const { error } = await supabase.from('prospect_invites').insert({
        una_id: unaId,
        created_by: user?.id,
        prospect_name: form.prospect_name.trim(),
        prospect_email: form.prospect_email.trim(),
        personal_note: form.personal_note.trim() || null,
        token,
      });
      if (error) throw error;
      setLastLink(linkFor(token));
      setForm({ prospect_name: '', prospect_email: '', personal_note: '' });
      toast.success('The link is ready to hand over.');
      load();
    } catch (err: any) {
      toast.error(err?.message || 'We could not make the link. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function copy(text: string) {
    await navigator.clipboard.writeText(text);
    toast.success('Link copied.');
  }

  async function loadDocumentsFor(applicationId: string) {
    if (!unaId) return;
    const { data } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('una_id', unaId)
      .order('generated_at', { ascending: true });
    setDocuments(
      (data || []).filter((d: any) => d.merge_field_data?.application_id === applicationId),
    );
  }

  async function openDetail(application: any) {
    setOpenApplication(application);
    setDocuments([]);
    await loadDocumentsFor(application.id);
  }

  async function decline(application: any) {
    const { error } = await supabase
      .from('prospect_applications')
      .update({ status: 'declined', decided_at: new Date().toISOString(), decided_by: user?.id })
      .eq('id', application.id);
    if (error) {
      toast.error('We could not record that. Please try again.');
      return;
    }
    toast.success('Recorded.');
    setOpenApplication(null);
    load();
  }

  async function pdfFromHtml(html: string): Promise<{ blob: Blob; size: number }> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('Please sign in again');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ html, author: una?.name || undefined }),
    });
    if (!response.ok) throw new Error('We could not build the file. Please try again.');
    const { pdf: pdfBase64, size } = await response.json();
    const chars = atob(pdfBase64);
    const bytes = new Uint8Array(chars.length);
    for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
    return { blob: new Blob([bytes], { type: 'application/pdf' }), size };
  }

  async function approve(application: any) {
    if (!unaId) return;
    setApproving(true);
    try {
      const { count: officerCount } = await supabase
        .from('association_offices')
        .select('id', { count: 'exact', head: true })
        .eq('association_id', unaId)
        .in('status', ['seated', 'active', 'filled']);

      const decisionDate = new Date().toISOString();
      const year = new Date(decisionDate).getFullYear();

      let certNo = '';
      if (includeCertificate) {
        const { count: approvedThisYear } = await supabase
          .from('prospect_applications')
          .select('id', { count: 'exact', head: true })
          .eq('una_id', unaId)
          .eq('status', 'approved')
          .gte('decided_at', `${year}-01-01`);
        certNo = certificateNumber(year, (approvedThisYear || 0) + 1);
      }

      const outputs: ApplicationOutput[] = buildApprovalOutputs({
        associationName: una?.name || 'the Association',
        officerRole: una?.primary_officer_role || 'Secretary Treasurer',
        applicantName: application.full_name,
        applicantCityState: application.city_state,
        decisionDate,
        certificateNumber: certNo,
        seatedOfficerCount: officerCount ?? 0,
        includeCertificate,
        sealEnabled: seal.sealEnabled,
        sealSvg: seal.sealSvg,
      });

      for (const output of outputs) {
        const { blob, size } = await pdfFromHtml(output.html);
        const filePath = generatedFilePath(una?.name, output.key, application.id);
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, blob, { contentType: 'application/pdf', upsert: true });
        if (uploadError) throw uploadError;

        const { error: insertError } = await supabase.from('generated_documents').insert({
          una_id: unaId,
          document_name: output.title,
          document_type: 'membership',
          file_url: filePath,
          file_path: filePath,
          file_size: size,
          status: 'draft',
          custom_content: output.html,
          created_by: user?.id,
          requires_signatures: true,
          merge_field_data: {
            template_key: output.key,
            application_id: application.id,
            certificate_number: certNo || null,
            is_draft_format: !APPLICATION_FORMATS_APPROVED,
            requires_signature: true,
            signing: output.signing,
            default_ink: output.defaultInk ?? false,
            signing_reason: output.signingReason ?? null,
            wet_ink_required: output.signing === 'wet_required',
            sealed: seal.sealEnabled,
          },
        });
        if (insertError) throw insertError;
      }

      // Keep the roster current using the same table the Add a UNA Member workflow writes to.
      const { error: rosterError } = await supabase.from('una_members').insert({
        una_id: unaId,
        full_name: application.full_name,
        email: application.email,
        phone: application.phone,
        role: 'Member',
        status: 'active',
        joined_date: decisionDate,
      });
      if (rosterError) throw rosterError;

      const { error: statusError } = await supabase
        .from('prospect_applications')
        .update({ status: 'approved', decided_at: decisionDate, decided_by: user?.id })
        .eq('id', application.id);
      if (statusError) throw statusError;

      toast.success('Approved. The papers are ready.');
      await loadDocumentsFor(application.id);
      setOpenApplication({ ...application, status: 'approved', decided_at: decisionDate });
      load();
    } catch (err: any) {
      toast.error(err?.message || 'We could not finish that. Please try again.');
    } finally {
      setApproving(false);
    }
  }

  const submitted = applications.filter((a) => a.status === 'submitted');

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Member applications</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Hand one person a private application link and read what comes back.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : (
          <Tabs defaultValue="invite">
            <TabsList>
              <TabsTrigger value="invite">Invite someone to apply</TabsTrigger>
              <TabsTrigger value="queue">Applications{submitted.length ? ` (${submitted.length})` : ''}</TabsTrigger>
            </TabsList>

            <TabsContent value="invite" className="space-y-4 pt-4">
              <Alert>
                <AlertDescription className="text-sm">{INVITE_TEACHING}</AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Who is this for</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="prospect_name">Their name</Label>
                    <Input
                      id="prospect_name"
                      value={form.prospect_name}
                      onChange={(e) => setForm({ ...form, prospect_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prospect_email">Their email</Label>
                    <Input
                      id="prospect_email"
                      type="email"
                      value={form.prospect_email}
                      onChange={(e) => setForm({ ...form, prospect_email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personal_note">A personal note, if you want one</Label>
                    <Textarea
                      id="personal_note"
                      rows={3}
                      value={form.personal_note}
                      onChange={(e) => setForm({ ...form, personal_note: e.target.value })}
                    />
                  </div>
                  <Button onClick={createInvite} disabled={saving} className="min-h-11">
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Making the link...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" /> Make the link
                      </>
                    )}
                  </Button>

                  {lastLink && (
                    <div className="rounded-lg border p-3 space-y-2">
                      <p className="text-sm break-all">{lastLink}</p>
                      <Button variant="outline" size="sm" className="min-h-11" onClick={() => copy(lastLink)}>
                        <Copy className="h-4 w-4 mr-2" /> Copy the link
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {invites.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Links you have handed out</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {invites.map((invite) => (
                      <div key={invite.id} className="rounded-lg border p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{invite.prospect_name}</span>
                          <Badge variant="outline">{invite.status}</Badge>
                          <span className="text-xs text-muted-foreground">
                            Expires {new Date(invite.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-11"
                          onClick={() => copy(linkFor(invite.token))}
                        >
                          <Copy className="h-4 w-4 mr-2" /> Copy the link
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="queue" className="space-y-3 pt-4">
              {applications.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing has come back yet.</p>
              )}
              {applications.map((application) => {
                const age = ageFromDateOfBirth(application.date_of_birth);
                return (
                  <button
                    key={application.id}
                    onClick={() => openDetail(application)}
                    className="w-full text-left rounded-lg border p-3 hover:bg-muted/40 min-h-11"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{application.full_name}</span>
                      {isMinor(application.date_of_birth) && <Badge variant="destructive">Minor</Badge>}
                      <Badge variant="outline">{application.status}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(application.submitted_at).toLocaleDateString()}
                        {age !== null ? `, age ${age}` : ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={!!openApplication} onOpenChange={(open) => !open && setOpenApplication(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{openApplication?.full_name}</DialogTitle>
          </DialogHeader>
          {openApplication && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-2">
                <div>City and state: {openApplication.city_state || 'Not given'}</div>
                <div>Email: {openApplication.email}</div>
                <div>Phone: {openApplication.phone || 'Not given'}</div>
                <div>
                  Date of birth: {openApplication.date_of_birth || 'Not given'}
                  {ageFromDateOfBirth(openApplication.date_of_birth) !== null
                    ? `, age ${ageFromDateOfBirth(openApplication.date_of_birth)}`
                    : ''}
                </div>
                <div>Sent in: {new Date(openApplication.submitted_at).toLocaleString()}</div>
                <div>Typed signature: {openApplication.typed_signature}</div>
              </div>

              <div className="space-y-2">
                <p className="font-medium">What they agreed to</p>
                {(openApplication.affirmations || []).map((a: any, i: number) => (
                  <div key={i} className="rounded border p-2">
                    <p>{a.text || AFFIRMATIONS[i]?.text}</p>
                    {a.agreed_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Agreed {new Date(a.agreed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {isMinor(openApplication.date_of_birth) && (
                <Alert>
                  <AlertDescription>{MINOR_NOTE}</AlertDescription>
                </Alert>
              )}

              {openApplication.status === 'submitted' && (
                <div className="space-y-3">
                  {!isMinor(openApplication.date_of_birth) && (
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="include-certificate"
                        checked={includeCertificate}
                        onCheckedChange={(v) => setIncludeCertificate(v === true)}
                        className="mt-0.5"
                      />
                      <Label htmlFor="include-certificate" className="text-sm font-normal leading-snug">
                        Prepare a Member Certificate (the member's own proof of membership, presented at a meeting; not
                        required)
                      </Label>
                    </div>
                  )}
                  <SealApplyCheckbox
                    available={seal.sealAvailable}
                    checked={seal.applySeal}
                    onChange={seal.setApplySeal}
                    id="application-apply-seal"
                  />
                  <div className="flex flex-wrap gap-2">
                    {isMinor(openApplication.date_of_birth) ? (
                      <Button asChild className="min-h-11">
                        <a href="/support/ask">Ask a person</a>
                      </Button>
                    ) : (
                      <Button onClick={() => approve(openApplication)} disabled={approving} className="min-h-11">
                        {approving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing the papers...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
                          </>
                        )}
                      </Button>
                    )}
                    <Button variant="outline" className="min-h-11" onClick={() => decline(openApplication)}>
                      Decline
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">{DECLINE_NOTE}</p>

              {documents.length > 0 && (
                <div className="space-y-3">
                  {!APPLICATION_FORMATS_APPROVED && (
                    <Alert>
                      <AlertDescription>
                        These formats are drafts. They are marked DRAFT on every page and are waiting on approval. Do
                        not sign them yet.
                      </AlertDescription>
                    </Alert>
                  )}
                  {documents.map((doc) => (
                    <div key={doc.id} className="rounded-lg border p-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{doc.document_name}</span>
                        {doc.merge_field_data?.is_draft_format && <Badge variant="outline">Draft format</Badge>}
                        {doc.merge_field_data?.signing === 'wet_required' && (
                          <Badge variant="secondary">Sign on paper, blue ink</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" className="min-h-11" onClick={() => setPreviewDoc(doc)}>
                          <Eye className="h-4 w-4 mr-2" /> Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-11"
                          onClick={() => downloadDocumentById(doc.id, { underscoreFilename: true })}
                        >
                          <Download className="h-4 w-4 mr-2" /> Download
                        </Button>
                      </div>
                      <DocumentSigningControl doc={doc} unaId={unaId} sealEnabled={seal.sealAvailable} onChanged={() => openApplication && loadDocumentsFor(openApplication.id)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {previewDoc && (
        <DocumentPreviewModal open={!!previewDoc} onClose={() => setPreviewDoc(null)} document={previewDoc} />
      )}
    </MainLayout>
  );
}
